import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BoatService from '../../services/BoatService';
import './Admin.css';

const BoatRegistry = ({ onBoatUpdate }) => {
  const { token, isAdmin, user } = useAuth();
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBoat, setEditingBoat] = useState(null);
  const [formData, setFormData] = useState({
    boatId: '',
    displayName: '',
    description: '',
    type: 'fishing',
    capacity: 0,
    color: '#1976d2',
    icon: 'boat'
  });

  // Debugging info
  const [debugInfo, setDebugInfo] = useState({
    token: null,
    isAdmin: false,
    userRole: null,
    apiResponses: []
  });

  // Fetch boats on component mount
  useEffect(() => {
    if (token) {
      fetchBoats();
      
      // Update debug info
      setDebugInfo(prev => ({
        ...prev,
        token: token ? token.substring(0, 10) + '...' : null,
        isAdmin: isAdmin(),
        userRole: user?.role
      }));
    }
  }, [token, isAdmin, user]);

  const fetchBoats = async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    
    try {
      console.log("Fetching boats with token:", token ? token.substring(0, 10) + '...' : 'no token');
      
      // Try the registry endpoint first
      try {
        const data = await BoatService.getBoatsRegistry(token);
        setBoats(data);
        
        // Log to debug info
        setDebugInfo(prev => ({
          ...prev,
          apiResponses: [...prev.apiResponses.slice(0, 4), {
            endpoint: '/api/boats/registry',
            status: 'success',
            data: data.length + ' boats',
            timestamp: new Date().toISOString()
          }]
        }));
      } catch (err) {
        console.error("Error with registry endpoint:", err);
        
        // Log to debug info
        setDebugInfo(prev => ({
          ...prev,
          apiResponses: [...prev.apiResponses.slice(0, 4), {
            endpoint: '/api/boats/registry',
            status: 'error',
            message: err.message,
            timestamp: new Date().toISOString()
          }]
        }));
        
        // Fall back to available boats endpoint
        try {
          const availableBoats = await BoatService.getAvailableBoats();
          setBoats(availableBoats);
          setError("Registry endpoint failed, showing available boats instead");
          
          // Log to debug info
          setDebugInfo(prev => ({
            ...prev,
            apiResponses: [...prev.apiResponses.slice(0, 4), {
              endpoint: '/api/boats',
              status: 'success',
              data: availableBoats.length + ' boats',
              timestamp: new Date().toISOString()
            }]
          }));
        } catch (fallbackErr) {
          setError('Failed to load boats: ' + err.message);
          
          // Log to debug info
          setDebugInfo(prev => ({
            ...prev,
            apiResponses: [...prev.apiResponses.slice(0, 4), {
              endpoint: '/api/boats',
              status: 'error',
              message: fallbackErr.message,
              timestamp: new Date().toISOString()
            }]
          }));
        }
      }
    } catch (err) {
      setError('Failed to load boats: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      boatId: '',
      displayName: '',
      description: '',
      type: 'fishing',
      capacity: 0,
      color: '#1976d2',
      icon: 'boat'
    });
  };

  const handleAddBoat = async (e) => {
    e.preventDefault();
    try {
      await BoatService.registerBoat(token, formData);
      fetchBoats();
      // Notify the parent component to refresh the map data
      if (onBoatUpdate) onBoatUpdate();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to add boat: ' + err.message);
    }
  };

  const handleEditClick = (boat) => {
    setEditingBoat(boat);
    setFormData({
      boatId: boat.boatId || (boat.lastLocation && boat.lastLocation.boatId),
      displayName: boat.displayName || boat.boatId || (boat.lastLocation && boat.lastLocation.boatId),
      description: boat.description || '',
      type: boat.type || 'fishing',
      capacity: boat.capacity || 0,
      color: boat.color || '#1976d2',
      icon: boat.icon || 'boat'
    });
    
    // Scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('.admin-form-container');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleUpdateBoat = async (e) => {
    e.preventDefault();
    try {
      const { boatId, ...updateData } = formData;
      await BoatService.updateBoat(token, boatId, updateData);
      fetchBoats();
      // Notify the parent component to refresh the map data
      if (onBoatUpdate) onBoatUpdate();
      setEditingBoat(null);
      resetForm();
    } catch (err) {
      setError('Failed to update boat: ' + err.message);
    }
  };

  const handleDeleteBoat = async (boatId) => {
    if (window.confirm('Are you sure you want to delete this boat?')) {
      try {
        await BoatService.deleteBoat(token, boatId);
        fetchBoats();
        // Notify the parent component to refresh the map data
        if (onBoatUpdate) onBoatUpdate();
      } catch (err) {
        setError('Failed to delete boat: ' + err.message);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingBoat(null);
    resetForm();
  };

  const getBoatId = (boat) => {
    return boat.boatId || (boat.lastLocation && boat.lastLocation.boatId);
  };

  const getBoatDisplayName = (boat) => {
    return boat.displayName || boat.boatId || (boat.lastLocation && boat.lastLocation.boatId);
  };

  if (!isAdmin()) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <h3>Admin access required</h3>
          <p>Your current role: {user?.role || 'Unknown'}</p>
          <p>You must be an admin to access the boat registry management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h2>नाव रजिस्ट्री प्रबंधन | Boat Registry Management</h2>
      
      {error && <div className="admin-error">{error}</div>}
      
      <div className="admin-actions">
        <button 
          className="admin-button" 
          onClick={() => {
            resetForm();
            setShowAddForm(true);
            setEditingBoat(null);
            
            // Scroll to form
            setTimeout(() => {
              const formElement = document.querySelector('.admin-form-container');
              if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }}
        >
          <span>➕</span> नई नाव जोड़ें | Add New Boat
        </button>
        <button 
          className="admin-button" 
          onClick={() => {
            fetchBoats();
            if (onBoatUpdate) onBoatUpdate();
          }}
        >
          <span>🔄</span> रिफ्रेश | Refresh
        </button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingBoat) && (
        <div className="admin-form-container">
          <h3>{editingBoat ? 'नाव अपडेट करें | Update Boat' : 'नई नाव जोड़ें | Add New Boat'}</h3>
          <form className="admin-form" onSubmit={editingBoat ? handleUpdateBoat : handleAddBoat}>
            <div className="form-group">
              <label htmlFor="boatId">नाव आईडी | Boat ID:</label>
              <input
                type="text"
                id="boatId"
                name="boatId"
                value={formData.boatId}
                onChange={handleInputChange}
                required
                disabled={!!editingBoat}
                placeholder="Enter unique boat ID"
              />
            </div>
            <div className="form-group">
              <label htmlFor="displayName">प्रदर्शन नाम | Display Name:</label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                required
                placeholder="Enter boat display name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">विवरण | Description:</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Enter boat description"
              />
            </div>
            <div className="form-group">
              <label htmlFor="type">प्रकार | Type:</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="fishing">मछली पकड़ने वाली नाव | Fishing Boat</option>
                <option value="passenger">यात्री नाव | Passenger Boat</option>
                <option value="cargo">मालवाहक नाव | Cargo Boat</option>
                <option value="patrol">पेट्रोल नाव | Patrol Boat</option>
                <option value="other">अन्य | Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="capacity">क्षमता | Capacity:</label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                min="0"
                placeholder="Enter boat capacity"
              />
            </div>
            <div className="form-group">
              <label htmlFor="color">रंग | Color:</label>
              <div className="color-input-container">
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                />
                <span className="color-preview" style={{ backgroundColor: formData.color }}></span>
              </div>
            </div>

            <div className="admin-form-buttons">
              <button type="submit" className="admin-button">
                {editingBoat ? '✅ अपडेट | Update' : '✅ जोड़ें | Add'}
              </button>
              <button
                type="button"
                className="admin-button cancel"
                onClick={() => {
                  if (editingBoat) {
                    handleCancelEdit();
                  } else {
                    setShowAddForm(false);
                    resetForm();
                  }
                }}
              >
                ❌ रद्द करें | Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Boats List */}
      <div className="admin-table-container">
        <h3>नावों की सूची | Boat List</h3>
        {loading ? (
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Loading boats...</p>
          </div>
        ) : boats.length === 0 ? (
          <div className="admin-empty">
            <p>कोई नाव नहीं मिली | No boats found</p>
            <button onClick={() => { resetForm(); setShowAddForm(true); }} className="admin-button">
              <span>➕</span> नई नाव जोड़ें | Add First Boat
            </button>
          </div>
        ) : (
          <div className="admin-responsive-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>आईडी | ID</th>
                  <th>नाम | Name</th>
                  <th>प्रकार | Type</th>
                  <th>क्षमता | Capacity</th>
                  <th>स्थिति | Status</th>
                  <th>कार्य | Actions</th>
                </tr>
              </thead>
              <tbody>
                {boats.map((boat) => (
                  <tr key={getBoatId(boat)}>
                    <td>{getBoatId(boat)}</td>
                    <td>{getBoatDisplayName(boat)}</td>
                    <td>{boat.type || 'N/A'}</td>
                    <td>{boat.capacity || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${boat.active !== false ? 'active' : 'inactive'}`}>
                        {boat.active !== false ? 'सक्रिय | Active' : 'निष्क्रिय | Inactive'}
                      </span>
                    </td>
                    <td className="action-buttons">
                      <button
                        className="action-button edit"
                        onClick={() => handleEditClick(boat)}
                        title="Edit boat"
                      >
                        संपादित करें | Edit
                      </button>
                      <button
                        className="action-button delete"
                        onClick={() => handleDeleteBoat(getBoatId(boat))}
                        title="Delete boat"
                      >
                        हटाएं | Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Debug Information (hidden in production) */}
      {process.env.NODE_ENV !== 'production' && (
        <div className="debug-info">
          <details>
            <summary>Debug Information</summary>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default BoatRegistry; 